import logger from '../utils/logger';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Home, XCircle } from 'lucide-react';
import billingService from '../services/billingService';
import { formatCurrency } from '../utils/currencyFormatter';
import { useBilling } from '../context/BillingContext';
import { useBrand } from '../context/BrandContext';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetBillingState } = useBilling();
  const { brand } = useBrand();
  
  const [loading, setLoading] = useState(true);
  const [planActivated, setPlanActivated] = useState(false);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const planCode = searchParams.get('plan_code');
  

  const processPayment = useCallback(async () => {
      if (!sessionId) {
        setError('Missing payment session information');
        setLoading(false);
        return;
      }

      try {
        const directStatusResult = await billingService.checkDirectPaymentStatus(sessionId);
        
        if (directStatusResult.success && directStatusResult.data) {
          const paymentData = directStatusResult.data;
          
          if (paymentData.error) {
            setError(paymentData.message || paymentData.error || 'Failed to verify payment status');
            setLoading(false);
            return;
          }
          
          setPaymentStatus(paymentData);
          
          // Handle Stripe payment status
          const status = paymentData.status;
          
          if (status === 'completed' || paymentData.payment_status === 'paid') {
            if (planCode) {
              const activateResult = await billingService.activateUserPlan(planCode, sessionId);
              
              if (activateResult.success) {
                setPlanActivated(true);
                // Reset billing state to force refresh of user plan data
                resetBillingState();
              } else {
                setError(activateResult.error || 'Failed to activate plan');
              }
            } else {
              setPlanActivated(true);
            }
          } else if (status === 'failed' || paymentData.payment_status === 'unpaid') {
            setError('Payment failed. Please try again or contact support.');
          } else if (status === 'pending') {
            setPlanActivated(true);
          } else {
            setPlanActivated(true);
          }
        } else {
          setError(directStatusResult.error || 'Failed to verify payment status');
        }
      } catch (error) {
  logger.error('Error processing payment:', error);
        setError('Failed to verify payment status. Please contact support.');
      }
      
      setLoading(false);
    }, [sessionId, planCode, resetBillingState]);

  useEffect(() => {
    processPayment();
  }, [processPayment]);

  const handleGoToDashboard = () => {
    // Reset billing state to ensure fresh data is loaded
    resetBillingState();
    navigate('/dashboard');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-white text-lg">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-[#1f2937] rounded-xl border border-gray-700/50 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Payment Error</h1>
          <p className="text-gray-300 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={handleGoToDashboard}
              className="w-full bg-[#a15507] hover:bg-[#924e06] text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Go to Dashboard
            </button>
            <button
              onClick={handleGoHome}
              className="w-full bg-[#374151] hover:bg-[#4B5563] text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-[#1f2937] rounded-xl border border-gray-700/50 p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
        <p className="text-gray-300 mb-6">
          {planCode 
            ? `Your plan has been activated successfully. Welcome to ${brand.name}!`
            : 'Your payment was processed successfully. Your plan will be activated shortly.'
          }
        </p>

        {paymentStatus && paymentStatus.session_id && (
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-6 text-left">
            <h3 className="text-white font-medium mb-2">Payment Details:</h3>
            <div className="space-y-1 text-sm text-gray-300">
              <p>Status: <span className="text-green-400">{paymentStatus.status}</span></p>
              <p>Amount: {formatCurrency(paymentStatus.amount_total, paymentStatus.currency?.toUpperCase())}</p>
              <p>Session ID: {paymentStatus.session_id}</p>
            </div>
          </div>
        )}

        {planActivated && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6">
            <p className="text-green-400 text-sm">
              ✓ Plan activated successfully
            </p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleGoToDashboard}
            className="w-full bg-[#a15507] hover:bg-[#924e06] text-white py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            Go to Dashboard
            <ArrowRight className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleGoHome}
            className="w-full bg-[#374151] hover:bg-[#4B5563] text-white py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" />
            Go Home
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-700/50">
          <p className="text-gray-400 text-sm">
            Thank you for choosing {brand.name}!
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
